import { useCallback, useEffect, useState } from "react";
import { Loader2, TrendingUp, TrendingDown, FileDown, FileSpreadsheet, Mail, Scale, Trophy } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../api";
import { RekapKendaraan } from "../components/RekapKendaraan";

const fmtRp = (v) => `Rp ${Math.round(v || 0).toLocaleString("id-ID")}`;
const fmtKg = (v) => `${Number(v || 0).toLocaleString("id-ID")} KG`;
const NAMA_BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const labelBulan = (b) => `${NAMA_BULAN[parseInt(b.slice(5, 7), 10) - 1]} '${b.slice(2, 4)}`;
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const presetRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (preset === "bulan_ini") return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
  if (preset === "bulan_lalu") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (preset === "kuartal_ini") { const q = Math.floor(m / 3) * 3; return [iso(new Date(y, q, 1)), iso(new Date(y, q + 3, 0))]; }
  if (preset === "tahun_ini") return [`${y}-01-01`, `${y}-12-31`];
  return null;
};

const fmtTgl = (t) => (t ? `${t.slice(8, 10)}-${t.slice(5, 7)}-${t.slice(0, 4)}` : "-");

const DetailSusutDialog = ({ target, onClose }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!target) { setData(null); return; }
    api.get("/laporan/susut/trips", { params: { bulan: target.bulan, plat: target.plat } })
      .then(({ data }) => setData(data)).catch(() => setData({ rows: [] }));
  }, [target]);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="detail-susut-dialog">
        <DialogHeader>
          <DialogTitle>Trip Melebihi Toleransi — {target?.plat} ({target?.bulan})</DialogTitle>
        </DialogHeader>
        {data === null ? (
          <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : data.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-400">Tidak ada trip yang melebihi toleransi pada bulan ini.</p>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="detail-susut-table">
              <thead className="sticky top-0 bg-zinc-50">
                <tr className="border-b border-zinc-200">
                  {["ID Trip", "Tanggal", "Supir", "Komoditas", "Muat (KG)", "Bongkar (KG)", "Selisih", "Toleransi", "Kelebihan", "Kerugian"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.ID_Trip} className="border-b border-zinc-100">
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-zinc-900">{r.ID_Trip}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtTgl(r.Tanggal_Muat)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.Nama_Supir}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.Jenis_Komoditas}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{Number(r.Tonase_Muat_KG || 0).toLocaleString("id-ID")}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{Number(r.Tonase_Bongkar_KG || 0).toLocaleString("id-ID")}</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-red-700">{Number(r.Penyusutan_KG).toLocaleString("id-ID")} KG</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-zinc-500">{Number(r.Toleransi_KG || 0).toLocaleString("id-ID")} KG</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-red-700 font-medium">{Number(r.Over_KG).toLocaleString("id-ID")} KG</td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap text-red-700 font-semibold">{fmtRp(r.Kerugian_Rp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Baris = ({ label, nilai, tebal, negatif, testId }) => (
  <div className={`flex items-center justify-between py-2 ${tebal ? "font-semibold text-zinc-900" : "text-zinc-700"}`}>
    <span className="text-sm pr-4">{label}</span>
    <span data-testid={testId} className={`num-hc whitespace-nowrap ${negatif ? "text-red-700" : "text-zinc-900"}`}>
      {negatif && nilai > 0 ? `(${fmtRp(nilai)})` : fmtRp(nilai)}
    </span>
  </div>
);

export default function LabaRugiPage() {
  const [preset, setPreset] = useState("bulan_ini");
  const [[dateFrom, dateTo], setRange] = useState(presetRange("bulan_ini"));
  const [plat, setPlat] = useState("semua");
  const [kendaraan, setKendaraan] = useState([]);
  const [data, setData] = useState(null);
  const [tren, setTren] = useState(null);
  const [trenSusut, setTrenSusut] = useState(null);
  const [bulanSusut, setBulanSusut] = useState(new Date().toISOString().slice(0, 7));
  const [peringkatSusut, setPeringkatSusut] = useState(null);
  const [detailSusut, setDetailSusut] = useState(null);
  const [perTruk, setPerTruk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api.get("/kendaraan").then(({ data }) => setKendaraan(data)).catch(() => {});
    api.get("/laporan/bulan-terakhir").then(({ data }) => {
      if (data.bulan && data.bulan !== data.bulan_ini) {
        const [y, m] = data.bulan.split("-").map(Number);
        setRange([iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))]);
        setPreset("kustom");
        setBulanSusut(data.bulan);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/laporan/laba-rugi/tren", { params: { plat } }).then(({ data }) => setTren(data)).catch(() => setTren([]));
    api.get("/laporan/susut/tren", { params: { plat } }).then(({ data }) => setTrenSusut(data)).catch(() => setTrenSusut([]));
  }, [plat]);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    api.get("/laporan/laba-rugi/per-truk", { params: { date_from: dateFrom, date_to: dateTo } })
      .then(({ data }) => setPerTruk(data)).catch(() => setPerTruk({ items: [] }));
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!bulanSusut) return;
    api.get("/laporan/susut/peringkat", { params: { bulan: bulanSusut } })
      .then(({ data }) => setPeringkatSusut(data)).catch(() => setPeringkatSusut(null));
  }, [bulanSusut]);

  const load = useCallback(() => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    api.get("/laporan/laba-rugi", { params: { date_from: dateFrom, date_to: dateTo, plat } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Gagal memuat laporan"))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, plat]);

  useEffect(() => { load(); }, [load]);

  const changePreset = (p) => {
    setPreset(p);
    const r = presetRange(p);
    if (r) setRange(r);
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const res = await api.get(`/laporan/laba-rugi/${fmt}`, {
        params: { date_from: dateFrom, date_to: dateTo, plat }, responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `Laba_Rugi_${dateFrom}_${dateTo}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Laporan ${fmt.toUpperCase()} berhasil diunduh`);
    } catch {
      toast.error(`Gagal mengekspor ${fmt.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  const bo = data?.biaya_operasional;
  const bp = data?.biaya_pemeliharaan;
  const su = data?.susut || { selisih_kg: 0, kerugian_rp: 0, jumlah_trip_over: 0 };
  const untung = (data?.laba_bersih ?? 0) >= 0;

  return (
    <div data-testid="laba-rugi-page" className="space-y-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-emerald-700" /> Laporan Laba Rugi
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Profit &amp; Loss — pendapatan, biaya operasional &amp; pemeliharaan otomatis</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button data-testid="export-pdf-button" variant="outline" onClick={() => exportFile("pdf")} disabled={!!exporting || !data}
            className="border-red-300 text-red-800 hover:bg-red-50 btn-touch">
            {exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Export PDF
          </Button>
          <Button data-testid="export-excel-button" variant="outline" onClick={() => exportFile("excel")} disabled={!!exporting || !data}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 btn-touch">
            {exporting === "excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Export Excel
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="pl-filter-bar">
        <Select value={preset} onValueChange={changePreset}>
          <SelectTrigger data-testid="pl-preset-select" className="bg-white sm:w-44 btn-touch"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="bulan_ini">Bulan Ini</SelectItem>
            <SelectItem value="bulan_lalu">Bulan Lalu</SelectItem>
            <SelectItem value="kuartal_ini">Kuartal Ini</SelectItem>
            <SelectItem value="tahun_ini">Tahun Ini</SelectItem>
            <SelectItem value="kustom">Kustom</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 min-w-0">
          <Input data-testid="pl-date-from" type="date" value={dateFrom}
            onChange={(e) => { setPreset("kustom"); setRange([e.target.value, dateTo]); }}
            className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40 input-touch" />
          <span className="text-xs text-zinc-400 shrink-0">s/d</span>
          <Input data-testid="pl-date-to" type="date" value={dateTo}
            onChange={(e) => { setPreset("kustom"); setRange([dateFrom, e.target.value]); }}
            className="bg-white min-w-0 flex-1 sm:flex-none sm:w-40 input-touch" />
        </div>
        <Select value={plat} onValueChange={setPlat}>
          <SelectTrigger data-testid="pl-plat-select" className="bg-white sm:w-52 btn-touch"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="semua">Semua Unit Truk</SelectItem>
            {kendaraan.map((k) => <SelectItem key={k.id} value={k.Plat_Nomor}>{k.Plat_Nomor}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-14 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          <Card className={`shadow-sm ${untung ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60"}`}>
            <CardContent className="pt-5 flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider ${untung ? "text-emerald-700" : "text-red-700"}`}>Laba Bersih Operasional</p>
                <p data-testid="laba-bersih-value" className={`text-3xl font-bold tabular-nums mt-1 ${untung ? "text-emerald-800" : "text-red-800"}`}>
                  {fmtRp(data.laba_bersih)}
                </p>
                {data.margin_persen !== null && (
                  <p data-testid="margin-label" className={`text-xs mt-1 ${untung ? "text-emerald-700/80" : "text-red-700/80"}`}>Margin {data.margin_persen}% dari pendapatan</p>
                )}
              </div>
              {untung ? <TrendingUp className="w-12 h-12 text-emerald-300" /> : <TrendingDown className="w-12 h-12 text-red-300" />}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-emerald-800">Pendapatan</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-zinc-100">
              <Baris label={`Pendapatan Jasa Angkut (${data.pendapatan.jumlah_invoice} invoice Lunas/Sebagian)`} nilai={data.pendapatan.total} testId="pendapatan-line" />
              <Baris label="TOTAL PENDAPATAN" nilai={data.pendapatan.total} tebal testId="pendapatan-total" />
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-amber-800">Biaya Operasional Langsung</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-zinc-100">
              <Baris label="Uang Jalan (Solar, Tol, Makan, dll.)" nilai={bo.uang_jalan} testId="uang-jalan-line" />
              <Baris label="Gaji Bulanan Supir (Upah Kotor)" nilai={bo.gaji_supir} testId="gaji-line" />
              <Baris label="Potongan Klaim Susut (pengurang gaji)" nilai={bo.potongan_klaim_susut} negatif testId="potongan-line" />
              <Baris label="TOTAL BIAYA OPERASIONAL" nilai={bo.subtotal} tebal testId="biaya-operasional-total" />
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-blue-800">Biaya Pemeliharaan</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-zinc-100">
              <Baris label={`Biaya Servis Bengkel (${bp.jumlah_servis} servis, termasuk suku cadang)`} nilai={bp.total_servis} testId="servis-line" />
              <p className="text-xs text-zinc-500 py-2">di antaranya pemakaian suku cadang gudang: <span className="tabular-nums font-medium">{fmtRp(bp.pemakaian_onderdil)}</span></p>
              <Baris label="TOTAL BIAYA PEMELIHARAAN" nilai={bp.subtotal} tebal testId="biaya-pemeliharaan-total" />
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-red-800">Kerugian Susut Tonase</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-zinc-100">
              <Baris label={`Kerugian Susut Melebihi Toleransi (${su.jumlah_trip_over} trip)`} nilai={su.kerugian_rp} testId="kerugian-susut-line" />
              <p className="text-xs text-zinc-500 py-2">
                Total selisih tonase periode:{" "}
                <span className={`tabular-nums font-medium ${su.selisih_kg > 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {Number(su.selisih_kg).toLocaleString("id-ID")} KG
                </span>{" "}
                — toleransi &amp; harga komoditas sesuai Pengaturan
              </p>
              <Baris label="TOTAL KERUGIAN SUSUT" nilai={su.kerugian_rp} tebal testId="kerugian-susut-total" />
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
                Tren Laba 12 Bulan Terakhir {plat !== "semua" && `— ${plat}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tren === null ? (
                <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : (
                <div className="h-56" data-testid="tren-laba-chart">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={tren} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                      <XAxis dataKey="bulan" tickFormatter={labelBulan} tick={{ fontSize: 11, fill: "#71717a" }} />
                      <YAxis tickFormatter={(v) => `${Math.round(v / 1000000)}jt`} tick={{ fontSize: 11, fill: "#71717a" }} width={40} />
                      <Tooltip formatter={(v) => [fmtRp(v), "Laba"]} labelFormatter={labelBulan} />
                      <ReferenceLine y={0} stroke="#a1a1aa" />
                      <Bar dataKey="laba" radius={[3, 3, 0, 0]}>
                        {tren.map((d) => <Cell key={d.bulan} fill={d.laba >= 0 ? "#047857" : "#dc2626"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
                Tren Susut 12 Bulan Terakhir {plat !== "semua" && `— ${plat}`}
              </CardTitle>
              <p className="text-xs text-zinc-500">Total selisih tonase per bulan (negatif = kehilangan tonase). Pilih unit truk di atas untuk melihat pola per truk.</p>
            </CardHeader>
            <CardContent>
              {trenSusut === null ? (
                <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : (
                <div className="h-56" data-testid="tren-susut-chart">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={trenSusut} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                      <XAxis dataKey="bulan" tickFormatter={labelBulan} tick={{ fontSize: 11, fill: "#71717a" }} />
                      <YAxis tickFormatter={(v) => `${v.toLocaleString("id-ID")}`} tick={{ fontSize: 11, fill: "#71717a" }} width={52} />
                      <Tooltip
                        labelFormatter={labelBulan}
                        formatter={(v, name, item) => [
                          `${Number(v).toLocaleString("id-ID")} KG (kerugian ${fmtRp(item?.payload?.kerugian_rp)})`,
                          "Susut",
                        ]}
                      />
                      <ReferenceLine y={0} stroke="#a1a1aa" />
                      <Bar dataKey="susut_kg" radius={[3, 3, 0, 0]}>
                        {trenSusut.map((d) => <Cell key={d.bulan} fill={d.susut_kg >= 0 ? "#047857" : "#dc2626"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-zinc-700">Peringkat Susut Truk</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Truk dengan kerugian susut terbesar diurutkan paling atas — klik baris truk untuk melihat trip yang melebihi toleransi</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="peringkat-susut-bulan" type="month" value={bulanSusut}
                    onChange={(e) => setBulanSusut(e.target.value)} className="w-44" />
                  <Button data-testid="peringkat-susut-export" type="button" variant="outline" size="sm"
                    disabled={!peringkatSusut || peringkatSusut.rows.length === 0}
                    onClick={async () => {
                      try {
                        const res = await api.get(`/laporan/susut/peringkat/export?bulan=${bulanSusut}`, { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `Peringkat_Susut_${bulanSusut}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                        toast.success("Excel peringkat susut diunduh");
                      } catch {
                        toast.error("Gagal mengunduh Excel");
                      }
                    }}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 transition-colors duration-200">
                    <FileDown className="w-4 h-4 mr-1.5" /> Excel
                  </Button>
                  <Button data-testid="peringkat-susut-email" type="button" variant="outline" size="sm"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/laporan/email-bulanan", { bulan: bulanSusut });
                        toast.success(`Laporan bulanan ${data.bulan} terkirim ke ${data.recipient}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Gagal mengirim email laporan");
                      }
                    }}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 transition-colors duration-200">
                    <Mail className="w-4 h-4 mr-1.5" /> Email
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!peringkatSusut || peringkatSusut.rows.length === 0 ? (
                <p data-testid="peringkat-susut-empty" className="py-6 text-center text-sm text-zinc-400">Tidak ada trip dengan data tonase pada bulan ini.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="peringkat-susut-table">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        {["#", "Unit Truk", "Trip", "Susut (KG)", "Trip Lebihi Toleransi", "Kerugian Susut"].map((h) => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {peringkatSusut.rows.map((r, i) => (
                        <tr key={r.plat} data-testid={`peringkat-susut-row-${r.plat.replace(/ /g, "-")}`}
                          onClick={() => setDetailSusut({ plat: r.plat, bulan: bulanSusut })}
                          className="border-b border-zinc-100 cursor-pointer hover:bg-emerald-50/50 transition-colors duration-150">
                          <td className="px-4 py-2.5 text-zinc-400">{i + 1}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap font-medium text-zinc-900">
                            {r.plat}
                            {i === 0 && r.kerugian_rp > 0 && (
                              <Badge className="ml-2 bg-red-100 text-red-800 border border-red-200 hover:bg-red-100">Perlu Ditindak</Badge>
                            )}
                          </td>
                          <td className="px-4 py-2.5 tabular-nums">{r.trip}</td>
                          <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap ${r.susut_kg > 0 ? "text-emerald-700" : r.susut_kg < 0 ? "text-red-700" : "text-zinc-400"}`}>{fmtKg(r.susut_kg)}</td>
                          <td className={`px-4 py-2.5 tabular-nums ${r.trip_over > 0 ? "text-red-700 font-medium" : "text-zinc-400"}`}>{r.trip_over}</td>
                          <td className={`px-4 py-2.5 tabular-nums whitespace-nowrap font-semibold ${r.kerugian_rp > 0 ? "text-red-700" : "text-zinc-400"}`}>{fmtRp(r.kerugian_rp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-zinc-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
                Perbandingan Laba Antar Truk ({dateFrom} s/d {dateTo})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {perTruk === null ? (
                <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : perTruk.items.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-6">Belum ada unit truk terdaftar.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="per-truk-table">
                      <thead>
                        <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                          <th className="px-3 py-2">#</th><th className="px-3 py-2">Unit Truk</th>
                          <th className="px-3 py-2 text-right">Trip</th>
                          <th className="px-3 py-2 text-right">Pendapatan</th>
                          <th className="px-3 py-2 text-right">Uang Jalan</th>
                          <th className="px-3 py-2 text-right">Biaya Servis</th>
                          <th className="px-3 py-2 text-right">Sparepart</th>
                          <th className="px-3 py-2 text-right">Laba</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perTruk.items.map((r, i) => (
                          <tr key={r.plat} data-testid={`per-truk-row-${r.plat}`} className="border-b border-zinc-100">
                            <td className="px-3 py-2.5 text-zinc-400">{i + 1}</td>
                            <td className="px-3 py-2.5 font-medium text-zinc-900 whitespace-nowrap">
                              <span className="inline-flex items-center gap-2">
                                {r.plat}
                                {i === 0 && r.laba > 0 && (
                                  <Badge className="bg-amber-100 text-amber-800 border border-amber-200 font-medium">
                                    <Trophy className="w-3 h-3 mr-1" /> Paling Untung
                                  </Badge>
                                )}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{r.trip}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{fmtRp(r.pendapatan)}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{fmtRp(r.uang_jalan)}</td>
                            <td data-testid={`per-truk-servis-${r.plat}`} className="px-3 py-2.5 text-right tabular-nums">
                              {fmtRp(r.jasa_servis)}
                              {r.n_servis > 0 && <span className="block text-[11px] text-zinc-400">{r.n_servis} servis</span>}
                            </td>
                            <td data-testid={`per-truk-sparepart-${r.plat}`} className="px-3 py-2.5 text-right tabular-nums">{fmtRp(r.sparepart)}</td>
                            <td className={`px-3 py-2.5 text-right num-hc ${r.laba >= 0 ? "text-emerald-800" : "text-red-700"}`}>{fmtRp(r.laba)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr data-testid="per-truk-total-row" className="bg-zinc-50 font-semibold text-zinc-900">
                          <td className="px-3 py-2.5" colSpan={2}>Total Semua Unit</td>
                          {["trip", "pendapatan", "uang_jalan", "jasa_servis", "sparepart", "laba"].map((k) => {
                            const v = perTruk.items.reduce((s, r) => s + (r[k] || 0), 0);
                            return <td key={k} className="px-3 py-2.5 text-right tabular-nums">{k === "trip" ? v : fmtRp(v)}</td>;
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">• {perTruk.catatan}</p>
                </>
              )}
            </CardContent>
          </Card>

          {data.catatan.length > 0 && (
            <div className="text-xs text-zinc-500 space-y-1" data-testid="pl-catatan">
              {data.catatan.map((c, i) => <p key={i}>• {c}</p>)}
            </div>
          )}

          <RekapKendaraan mode="standar" title="Ringkasan Kendaraan Bulanan (Harian)" />

          <DetailSusutDialog target={detailSusut} onClose={() => setDetailSusut(null)} />
        </>
      )}
    </div>
  );
}
